/**
 * Utilitaires d'export et de partage pour "Mes Ventes".
 * Tout est fait côté navigateur, sans appel API.
 */

import type { VenteDto } from '../api/types';
import { fmtDate, fmtEur } from './format';

// ─── Produits considérés comme "immobilier" (utilisent le prix TTC) ─
const IMMO_PRODS = new Set(['immo-vente', 'immo-apport', 'scpi']);

export function isImmo(v: VenteDto): boolean {
  return IMMO_PRODS.has(v.prod);
}

/**
 * Format euros sécurisé pour le rendu PDF.
 * Le toLocaleString('fr-FR') insère des espaces Unicode (U+202F = NARROW NO-BREAK SPACE,
 * U+00A0 = NO-BREAK SPACE) que la police Helvetica embarquée dans jsPDF ne sait pas afficher
 * correctement (elle les remplace par "/" ou les fait sauter visuellement).
 * On normalise tous ces espaces en espace ASCII classique pour avoir "26 000 €" lisible.
 */
function eurPdf(n: number): string {
  return sanitizeForPdf(fmtEur(n));
}

/** Idem mais pour fmtDate (par sécurité, certains locales injectent des U+00A0). */
function datePdf(iso: string): string {
  return sanitizeForPdf(fmtDate(iso));
}

function sanitizeForPdf(s: string): string {
  return s
    .replace(/\u202F/g, ' ')   // narrow no-break space → espace simple
    .replace(/\u00A0/g, ' ');  // no-break space → espace simple
}

// ─── Génération CSV (séparateur ; pour Excel français) ──────────────
export function buildCSV(ventes: VenteDto[]): string {
  const headers = [
    'Date', 'Client', 'Entité', 'Produit', 'Sous-type',
    'Origine', 'Montant', 'Frais (%)', 'Commission', 'Collecte', 'Score',
    'Agence', 'Statut', 'Notes'
  ];

  const escape = (val: unknown): string => {
    const s = String(val ?? '');
    // Si la valeur contient ; " ou retour à la ligne, on encadre de guillemets et on échappe les " internes
    if (/[";\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const rows = ventes.map(v => [
    fmtDate(v.dateVente),
    `${v.clientPrenom} ${v.clientNom}`,
    v.entite === 'srx' ? 'Soronext' : 'Quintésens',
    v.prodLabel,
    [v.ppType, v.lodeomType, v.scpiMode, v.dispositif].filter(Boolean).join(' / '),
    v.origine === 'leads' ? 'Leads' : 'Recommandation',
    v.montant,
    v.frais ?? '',
    v.commission,
    v.collecte,
    v.score,
    v.agence,
    v.statut === 'sal' ? 'VRP' : 'MIA',
    v.notes ?? ''
  ].map(escape).join(';'));

  return [headers.join(';'), ...rows].join('\n');
}

export function downloadCSV(ventes: VenteDto[], filename: string): void {
  if (ventes.length === 0) {
    alert('Aucune vente à exporter.');
    return;
  }
  // BOM UTF-8 pour qu'Excel Windows lise correctement les accents
  const csv = '\uFEFF' + buildCSV(ventes);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
}

// ─── Génération PDF via window.print() ──────────────────────────────
// ─── Génération PDF via jsPDF ───────────────────────────────────────
// On utilise jsPDF plutôt que window.open()+print() car :
//   1. window.open ne marche pas sur mobile (PWA en plein écran)
//   2. Pas de "retour arrière" après print → mauvaise UX
//   3. jsPDF déclenche un téléchargement classique, on reste dans l'app
export async function exportPDF(
  ventes: VenteDto[],
  periodLabel: string,
  userName: string
): Promise<void> {
  if (ventes.length === 0) {
    alert('Aucune vente à exporter.');
    return;
  }

  // Import dynamique : jsPDF ~200 Ko, on ne le charge qu'au besoin
  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();      // 210 mm
  const H = doc.internal.pageSize.getHeight();     // 297 mm
  const M = 16;                                    // marge généreuse

  // ─── Couleurs identité ─────────────────────────────────
  const TEAL: [number, number, number] = [33, 179, 163];
  const TEAL_DARK: [number, number, number] = [24, 138, 125];
  const INK:  [number, number, number] = [13, 13, 13];
  const SUB:  [number, number, number] = [110, 110, 110];
  const DIM:  [number, number, number] = [180, 180, 180];

  // ─── Logo "com€asy" rendu via canvas SVG ────────────────
  // On dessine le logo dans un canvas haute résolution puis on l'insère
  // comme image dans le PDF. Ça garantit un rendu IDENTIQUE à la maquette.
  const logoImg = await renderLogoToImage(36); // 36mm de large, sera converti en px

  const logoY = M;
  const logoW = 36;       // mm
  const logoH = 15;       // mm (ratio approximatif du logo)
  doc.addImage(logoImg, 'PNG', M, logoY, logoW, logoH);

  // Bloc droite : date d'édition + nb ventes
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...SUB);
  const today = sanitizeForPdf(new Date().toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric'
  }));
  doc.text(`Édité le ${today}`, W - M, logoY + 5, { align: 'right' });
  doc.text(`${ventes.length} vente${ventes.length > 1 ? 's' : ''}`, W - M, logoY + 11, { align: 'right' });

  // Ligne de séparation teal fine
  const sepY = logoY + logoH + 4;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.4);
  doc.line(M, sepY, W - M, sepY);

  // ─── Titre + utilisateur ───────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...INK);
  doc.text(`Récapitulatif — ${periodLabel}`, M, sepY + 9);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...SUB);
  doc.text(userName, M, sepY + 15);

  // ─── Bloc résumé (KPIs) ────────────────────────────────
  const collecte = ventes.filter(v => !isImmo(v)).reduce((s, v) => s + v.collecte, 0);
  const prixTTC  = ventes.filter(v =>  isImmo(v)).reduce((s, v) => s + v.montant, 0);
  const totalCom = ventes.reduce((s, v) => s + v.commission, 0);

  // On affiche uniquement les KPIs pertinents (pas de "Prix TTC immo" à 0 si aucune vente immo)
  type Kpi = { lbl: string; val: string; color: [number, number, number] };
  const allKpis: Kpi[] = [];
  if (collecte > 0)  allKpis.push({ lbl: 'Collecte',      val: eurPdf(collecte), color: INK });
  if (prixTTC > 0)   allKpis.push({ lbl: 'Prix TTC immo', val: eurPdf(prixTTC),  color: INK });
  allKpis.push({ lbl: 'Commissions', val: eurPdf(totalCom), color: TEAL_DARK });

  const summaryY = sepY + 22;
  const summaryH = 20;
  doc.setFillColor(246, 255, 253);
  doc.setDrawColor(208, 236, 232);
  doc.setLineWidth(0.2);
  doc.roundedRect(M, summaryY, W - 2 * M, summaryH, 2, 2, 'FD');

  const kpiWidth = (W - 2 * M) / allKpis.length;
  allKpis.forEach((k, i) => {
    const cx = M + kpiWidth * i + kpiWidth / 2;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...SUB);
    doc.text(k.lbl.toUpperCase(), cx, summaryY + 7, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...k.color);
    doc.text(k.val, cx, summaryY + 14, { align: 'center' });
  });

  // ─── Tableau des ventes ────────────────────────────────
  let tableY = summaryY + summaryH + 10;
  const ROW_H = 8;

  // En-têtes
  doc.setFillColor(247, 248, 250);
  doc.rect(M, tableY, W - 2 * M, 8, 'F');
  doc.setDrawColor(220, 224, 230);
  doc.setLineWidth(0.2);
  doc.line(M, tableY + 8, W - M, tableY + 8);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(107, 111, 134);
  // Colonnes
  doc.text('DATE',       M + 2,        tableY + 5.5);
  doc.text('CLIENT',     M + 22,       tableY + 5.5);
  doc.text('PRODUIT',    M + 70,       tableY + 5.5);
  doc.text('MONTANT',    W - M - 38,   tableY + 5.5, { align: 'right' });
  doc.text('COMMISSION', W - M - 2,    tableY + 5.5, { align: 'right' });

  tableY += 8;

  // Lignes
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  for (let i = 0; i < ventes.length; i++) {
    const v = ventes[i];

    // Saut de page
    if (tableY + ROW_H > H - 22) {
      doc.addPage();
      tableY = M;
    }

    // Zébrure
    if (i % 2 === 1) {
      doc.setFillColor(251, 251, 252);
      doc.rect(M, tableY, W - 2 * M, ROW_H, 'F');
    }

    const subProd = [v.ppType, v.lodeomType, v.scpiMode, v.dispositif]
      .filter(Boolean).join(' / ');
    const prodLabel = subProd ? `${v.prodLabel} · ${subProd}` : v.prodLabel;
    const clientName = `${v.clientPrenom} ${v.clientNom}`;

    doc.setTextColor(...INK);
    doc.text(datePdf(v.dateVente),                              M + 2,  tableY + 5.5);
    doc.text(truncate(clientName, 24),                          M + 22, tableY + 5.5);
    doc.setTextColor(...SUB);
    doc.text(truncate(prodLabel, 36),                           M + 70, tableY + 5.5);
    doc.setTextColor(...INK);
    doc.text(eurPdf(v.montant),                                 W - M - 38, tableY + 5.5, { align: 'right' });

    doc.setTextColor(...TEAL_DARK);
    doc.setFont('helvetica', 'bold');
    doc.text(eurPdf(v.commission),                              W - M - 2, tableY + 5.5, { align: 'right' });
    doc.setFont('helvetica', 'normal');

    // Ligne de séparation très fine entre rangées
    doc.setDrawColor(238, 240, 244);
    doc.setLineWidth(0.1);
    doc.line(M, tableY + ROW_H, W - M, tableY + ROW_H);

    tableY += ROW_H;
  }

  // ─── Ligne "Total commissions" ─────────────────────────
  tableY += 2;
  if (tableY + 10 > H - 22) {
    doc.addPage();
    tableY = M;
  }
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.line(M, tableY, W - M, tableY);
  tableY += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text('Total commissions', M + 2, tableY);
  doc.setTextColor(...TEAL_DARK);
  doc.setFontSize(12);
  doc.text(eurPdf(totalCom), W - M - 2, tableY, { align: 'right' });

  // ─── Pied de page ──────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...DIM);
    doc.text(
      `${userName} · ${periodLabel} · Édité ${today}`,
      M, H - 8
    );
    doc.text(
      `ComEasy · Groupe Pluralle`,
      W - M, H - 8, { align: 'right' }
    );
    if (pageCount > 1) {
      doc.text(`${p} / ${pageCount}`, W / 2, H - 8, { align: 'center' });
    }
  }

  // ─── Téléchargement ────────────────────────────────────
  const filename = `comeasy_${slug(periodLabel)}_${todayCompactIso()}.pdf`;
  doc.save(filename);
}

/**
 * Rend le logo SVG "com€asy" dans un canvas haute résolution
 * et retourne le data-URL PNG. Garantit un rendu identique à la maquette
 * et au logo affiché dans le header de l'app.
 */
async function renderLogoToImage(_widthMm: number): Promise<string> {
  // SVG source — REPRODUCTION FIDÈLE du logo "com€asy" du Header
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 50" preserveAspectRatio="xMinYMid meet">
  <style>
    .cea-main { font-family: 'Helvetica', Arial, sans-serif; font-weight: 300; }
    .cea-tagline { font-family: 'Georgia', serif; font-style: italic; font-weight: 400; }
  </style>
  <text x="0" y="36" class="cea-main" font-size="34" letter-spacing="-2">
    <tspan fill="#21b3a3" font-size="42" font-weight="300">c</tspan><tspan fill="#0d0d0d">om</tspan><tspan fill="#C89010" font-size="25" font-weight="300">€</tspan><tspan fill="#0d0d0d">as</tspan>
  </text>
  <path d="M 101 20 L 107 34 M 119 8 L 100 49" fill="none" stroke="#C89010" stroke-width="0.5" stroke-linecap="round"/>
  <line x1="7" y1="44" x2="13" y2="44" stroke="#21b3a3" stroke-width="0.3" opacity="0.7"/>
  <text x="46.5" y="45" text-anchor="middle" class="cea-tagline" font-size="6.5" fill="rgba(0,0,0,0.5)">Vendez. Déclarez. Brillez.</text>
</svg>`;

  // Encodage du SVG en data-URL
  const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);

  // Décodage via Image → canvas
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Canvas 4x la taille pour qualité d'impression (PDF à 300 DPI)
      const scale = 4;
      const canvas = document.createElement('canvas');
      canvas.width  = 120 * scale;   // ratio SVG
      canvas.height = 50  * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas non disponible')); return; }
      // Fond blanc pour bien voir le logo (sinon transparence dans le PDF peut donner du gris)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error("Impossible de rendre le logo SVG."));
    img.src = svgUrl;
  });
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

function slug(s: string): string {
  return s.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function todayCompactIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

// ─── Partage natif (Web Share API) avec fallback clipboard ──────────
export async function shareRecap(ventes: VenteDto[], periodLabel: string): Promise<string> {
  const collecte = ventes.filter(v => !isImmo(v)).reduce((s, v) => s + v.collecte, 0);
  const totalCom = ventes.reduce((s, v) => s + v.commission, 0);
  const nb = ventes.length;

  const text = `📊 ComEasy — ${periodLabel}
${nb} vente${nb > 1 ? 's' : ''}
💰 Commissions : ${fmtEur(totalCom)}
📥 Collecte : ${fmtEur(collecte)}`;

  // Web Share API : disponible sur mobile + Safari, pas Chrome Desktop
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (typeof nav.share === 'function') {
    try {
      await nav.share({ title: 'ComEasy — Récapitulatif', text });
      return 'shared';
    } catch (err) {
      // Annulation utilisateur ou erreur — on ne fait rien, pas d'alerte
      if ((err as Error).name === 'AbortError') return 'cancelled';
      // Sinon, fallback clipboard
    }
  }

  // Fallback : copier dans le presse-papier
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    // Vraiment vieux navigateur : on revient au bon vieux prompt
    prompt('Copiez ce récap :', text);
    return 'fallback';
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 1500);
}
